import { defineStore } from 'pinia'

interface SpinnerState {
  text: string
  loadingCount: number
}

/**
 * Store to manage a global loading spinner state.
 *
 * Usage guidelines:
 * - Use `incrementLoadingCount()` and `decrementLoadingCount()` together to track the number of asynchronous operations that triggers loading.
 * - Use `setSpinnerText()` to customize the spinner message and `resetSpinnerText()` to restore the default text.
 */
export const useSpinnerStore = defineStore('SpinnerStore', {
  state: (): SpinnerState => ({
    text: 'Loading...',
    loadingCount: 0
  }),
  getters: {
    /**
     * Returns true if at least one loading operation is in progress.
     */
    isVisible: (state) => state.loadingCount > 0
  },
  actions: {
    /**
     * Sets the spinner text to a custom message.
     *
     * @param newText - The custom loading message to display.
     */
    setSpinnerText (newText: string) {
      this.text = newText
    },
    /**
     * Resets to the default spinner text
     */
    resetSpinnerText () {
      this.text = 'Loading...'
    },
    /**
     * Increments the loading count. Call this before a loading operation starts.
     *
     * Always pair this with a `decrementLoadingCount()` call after the operation finishes.
     */
    incrementLoadingCount () {
      this.loadingCount++
    },
    /**
     * Decrements the loading count. Call this after a loading operation finishes.
     *
     * Does nothing if the count is already zero.
     */
    decrementLoadingCount () {
      if (this.loadingCount > 0) {
        this.loadingCount--
      }
    },
    /**
     * Increments the loading count during the execution of a function optionally with spinner text. Decrements the
     * loading count after the method has executed. Will also restore the spinner text to its previous value after
     * completion if an override was provided.
     *
     * TODO: If we actually need to have the messsages be stackable (spin on multiple messages with arbitrary durations)
     * we need to store a list of messages tied to the promises, ordered in priority. Not a counter + message.
     *
     * @template ReturnType Return type of the promise returned by `callback`
     * @param callback Procedure to execute while the spinner should be spinning
     * @param spinnerText Optional text to display for the spinner. If undefined, it will use the current `text` value.
     * @returns Promise which resolves after the callback executes. Returns the value of the callback if any.
     */
    async spinWhile<ReturnType> (callback: () => Promise<ReturnType>, spinnerText?: string): Promise<ReturnType> {
      let oldText = null
      this.incrementLoadingCount()
      if (spinnerText !== undefined) {
        // Copy the text that was stored previously
        oldText = this.text
        this.setSpinnerText(spinnerText)
      }
      try {
        return await callback()
      } finally {
        this.decrementLoadingCount()
        if (oldText !== null) {
          // Reset the text to what is was before
          this.setSpinnerText(oldText)
        }
      }
    }
  }
})

export type SpinnerStore = ReturnType<typeof useSpinnerStore>